import { useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import {
  Button,
  DialogContent,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPremium, type GridColDef } from '@mui/x-data-grid-premium';
import { Trash2, UserPlus } from 'lucide-react';

import {
  Activity_Types_Enum_Enum,
  type GetLeadAgentsQuery,
  type UserSelectFragment,
} from '@graphql/__generated__/graphql';
import { GET_LEAD_ASSIGN_STAGE_QUERY } from '@modules/_shared/common/queries';
import { AutoAssignAssistant } from '@modules/_shared/components/AutoAssignAssistant';
import { ConfirmDialog } from '@modules/_shared/components/ConfirmDialog';
import IndicatorLeadStage from '@modules/_shared/components/IndicatorLeadStage';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { UserSelect } from '@modules/_shared/components/user-select/UserSelect';
import { UserAvatar } from '@modules/_shared/components/UserAvatar';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { isOwnerInitiatedLeadAgentSource } from '@modules/_shared/utils/leadAgentSources';
import { getAssignableLeadTeamIds } from '@modules/_shared/utils/leadAssignment';
import { GET_LEAD_AGENT_STATUS } from '@modules/lead-details/components/cards/LeadDetailsStatusCard';
import {
  ADD_LEAD_AGENT,
  DELETE_LEAD_AGENT,
  GET_LEAD,
  GET_LEADS,
  GET_LEAD_AGENTS,
  GET_LEAD_AGENT_SELECT_CARD_QUERY,
  LEAD_FEED,
} from '@modules/leads/leadsQueries';
import { useLocale } from '@shared/hooks/locale';

interface LeadAgentsProps {
  leadId: string;
  onChange?: () => void;
}

type LeadAgent = GetLeadAgentsQuery['lead_agents'][number];

export const LeadAgents = ({ leadId, onChange }: LeadAgentsProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const isAdmin = me?.is_admin === true;
  const tenantId = me?.tenant?.id;
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [showUserSelect, setShowUserSelect] = useState<boolean>(false);
  const [pendingAgent, setPendingAgent] = useState<UserSelectFragment | null>(
    null,
  );

  const { data, loading } = useQuery(GET_LEAD_AGENTS, {
    variables: { lead_id: leadId },
  });
  const lead_agents = data?.lead_agents ?? [];
  const [ownerTeamId] = getAssignableLeadTeamIds(
    data?.leads_by_pk?.owner_team_id,
    [],
  );

  const [getAssignStage] = useLazyQuery(GET_LEAD_ASSIGN_STAGE_QUERY);

  const [addLeadAgent, { loading: addLeadAgentLoading }] = useMutation(
    ADD_LEAD_AGENT,
    {
      refetchQueries: [
        GET_LEAD,
        LEAD_FEED,
        GET_LEADS,
        GET_LEAD_AGENTS,
        GET_LEAD_AGENT_SELECT_CARD_QUERY,
        GET_LEAD_AGENT_STATUS,
      ],
      onCompleted: () => {
        emitFeedRefresh();
        onChange?.();
      },
      onError: setErrorLike,
      awaitRefetchQueries: true,
    },
  );

  const [deleteLeadAgent, { loading: deleteLeadAgentLoading }] = useMutation(
    DELETE_LEAD_AGENT,
    {
      refetchQueries: [
        GET_LEAD,
        GET_LEADS,
        GET_LEAD_AGENTS,
        GET_LEAD_AGENT_SELECT_CARD_QUERY,
        GET_LEAD_AGENT_STATUS,
      ],
      onCompleted: () => {
        emitFeedRefresh();
        onChange?.();
      },
      onError: setErrorLike,
      awaitRefetchQueries: true,
    },
  );

  const columns: GridColDef<LeadAgent>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('Agent'),
        width: 200,
        display: 'flex',
        renderCell: params => (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <UserAvatar user={params.row.user} />
            <Stack>
              <Link
                href={`/users/${params.row.user.id}/overview`}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                color="inherit"
              >
                <Typography variant="body2" noWrap>
                  {params.row.user.first_name} {params.row.user.last_name}
                </Typography>
              </Link>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  color: 'text.secondary',
                }}
              >
                {params.row.user.default_team?.name}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        field: 'source',
        headerName: t('Source'),
        width: 100,
        display: 'flex',
        renderCell: params => (
          <Stack>
            <Typography variant="body2" noWrap>
              {params.row.source}
            </Typography>
            <Typography
              variant="body2"
              noWrap
              sx={{
                color: 'text.secondary',
              }}
            >
              <TimeAgo dateString={params.row.created_at} />
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'status',
        headerName: t('Status'),
        width: 100,
        display: 'flex',
        renderCell: params => (
          <Stack>
            <Typography variant="body2" noWrap>
              {params.row.passed_at != null
                ? t('Passed')
                : params.row.claimed_at
                ? t('Claimed')
                : t('Pending')}
            </Typography>
            <Typography
              variant="body2"
              noWrap
              sx={{
                color: 'text.secondary',
              }}
            >
              <TimeAgo
                dateString={
                  params.row.passed_at ??
                  params.row.claimed_at ??
                  params.row.created_at
                }
              />
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'stage',
        headerName: t('Stage'),
        width: 100,
        display: 'flex',
        renderCell: params => {
          if (!params.row.lead_stage?.label) {
            return '-';
          }
          return (
            <IndicatorLeadStage status={params.row.lead_stage.status}>
              {params.row.lead_stage.label}
            </IndicatorLeadStage>
          );
        },
      },
      {
        field: 'win_probability',
        headerName: t('Win probability'),
        width: 100,
        display: 'flex',
        valueGetter: (_, row) => row.win_probability,
      },
      {
        field: 'predicted_win_date',
        headerName: t('Predicted win date'),
        width: 100,
        display: 'flex',
        valueGetter: (_, row) => row.predicted_win_date,
      },
      {
        field: 'actions',
        headerName: t('Actions'),
        width: 80,
        sortable: false,
        filterable: false,
        display: 'flex',
        renderCell: params => (
          <Tooltip
            title={
              ownerTeamId != null
                ? params.row.passed_at == null
                  ? t(
                      'Assign another broker before removing the current profile lead owner.',
                    )
                  : t('Past assignments are kept as history.')
                : ''
            }
          >
            <span>
              <Button
                size="small"
                disabled={
                  isOwnerInitiatedLeadAgentSource(params.row.source) ||
                  ownerTeamId != null
                }
                onClick={async () => {
                  if (
                    window.confirm(
                      t(
                        'Are you sure you want to dismiss this agent? Remember to clean up any pending activities for this user!',
                      ),
                    )
                  ) {
                    deleteLeadAgent({
                      variables: {
                        id: params.row.id,
                        lead_id: leadId,
                        user_id: params.row.user.id,
                      },
                    });
                  }
                }}
              >
                <Trash2 size={16} />
              </Button>
            </span>
          </Tooltip>
        ),
      },
    ],
    [deleteLeadAgent, leadId, ownerTeamId, t],
  );

  const isLoading = loading || addLeadAgentLoading || deleteLeadAgentLoading;

  const hasAgents = (lead_agents ?? []).length > 0;

  return (
    <Stack sx={{ minWidth: 0, overflowX: 'hidden' }}>
      {hasAgents && (
        <DataGridPremium<LeadAgent>
          rows={lead_agents || []}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          autoHeight
          getRowId={row => row.id}
          hideFooter={true}
          columnHeaderHeight={36}
          sx={{
            border: 'none',
            backgroundColor: 'background.paper',
            minWidth: 0,
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: 'none',
            },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '0.75rem',
              fontWeight: 500,
              color: 'text.secondary',
            },
            '& .MuiDataGrid-columnSeparator': {
              color: 'divider',
            },
          }}
        />
      )}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{
          p: 2,
          gap: 1,
          minHeight: 56,
        }}
      >
        {showUserSelect ? (
          <UserSelect
            autoFocus
            openOnMount
            sx={{ width: '100%' }}
            onClose={(_, reason) => {
              if (reason !== 'selectOption') {
                setShowUserSelect(false);
              }
            }}
            onSelectedUsersChange={selected => {
              if (Array.isArray(selected)) {
                return;
              }
              if (selected != null) {
                setPendingAgent(selected);
              }
              setShowUserSelect(false);
            }}
            filters={{
              is_broker: { _eq: true },
              ...(ownerTeamId != null
                ? {
                    teams_users: {
                      team_id: {
                        _eq: ownerTeamId,
                      },
                      deactivated: { _eq: false },
                    },
                  }
                : {}),
              id: {
                _nin: lead_agents
                  ?.filter(agent => agent.passed_at == null)
                  ?.map(agent => agent.user.id),
              },
            }}
          />
        ) : (
          <Button
            onClick={() => setShowUserSelect(true)}
            variant="soft"
            startIcon={<UserPlus />}
            sx={{ flex: 1 }}
          >
            {ownerTeamId != null ? t('Reassign') : t('Add agent')}
          </Button>
        )}
        {isAdmin && !showUserSelect && (
          <AutoAssignAssistant leadId={leadId} tenantId={tenantId} />
        )}
      </Stack>
      <ConfirmDialog
        open={pendingAgent != null}
        title={t('Add {{name}} as an agent on this lead?', {
          name:
            pendingAgent?.full_name ??
            pendingAgent?.primaryEmail?.[0]?.email ??
            '',
        })}
        isSubmitting={addLeadAgentLoading}
        okText={ownerTeamId != null ? t('Reassign') : t('Add agent')}
        onClose={() => setPendingAgent(null)}
        onSubmit={async () => {
          if (pendingAgent == null) {
            return;
          }

          const { data: assignStage } = await getAssignStage({
            variables: {
              pipeline_id: me?.tenant.default_pipeline_id ?? '',
            },
          });

          await addLeadAgent({
            variables: {
              lead_id: leadId,
              user_id: pendingAgent.id,
              stage_id: assignStage?.lead_stages[0]?.id,
              activity_object: {
                lead_id: leadId,
                assigned_to: pendingAgent.id,
                activity_type: Activity_Types_Enum_Enum.Assignment,
                done: false,
                due_at: 'now()',
                ...(me?.is_admin ? { created_by: me?.id } : {}),
              },
            },
          });
          setPendingAgent(null);
        }}
      >
        <DialogContent>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <UserAvatar user={pendingAgent} size={48} />
            <Stack spacing={0.25}>
              <Typography variant="subtitle1" noWrap sx={{ lineHeight: 1.3 }}>
                {pendingAgent?.full_name ?? ''}
              </Typography>
              {isAdmin && pendingAgent?.primaryEmail?.[0]?.email && (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ color: 'text.secondary' }}
                >
                  {pendingAgent.primaryEmail[0].email}
                </Typography>
              )}
            </Stack>
          </Stack>
        </DialogContent>
      </ConfirmDialog>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </Stack>
  );
};
